import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth';
import toast from 'react-hot-toast';
import { Bell, BellOff } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import {
  isNotifySupported,
  isPushSupported,
  notifyPermission,
  getNotifyPref,
  setNotifyPref,
  requestNotifyPermission,
  showBrowserNotification,
  enablePushSubscription,
  disablePushSubscription,
  hasPushSubscription,
} from '../lib/browserNotifications';
import { pushApi } from '../api/push';

export default function Profile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  // Browser notification state — permission lives in the browser, the on/off
  // preference in localStorage, and the Web Push subscription (for delivery
  // with the app closed) in the browser's push manager + our backend.
  const [notifyOn, setNotifyOn] = useState(getNotifyPref());
  const [permission, setPermission] = useState(notifyPermission());
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    hasPushSubscription().then(setPushOn).catch(() => undefined);
  }, []);

  const enableNotifications = async () => {
    setBusy(true);
    try {
      const perm = await requestNotifyPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Notifications are blocked by the browser — allow them in your browser\'s site settings');
        return;
      }
      setNotifyPref(true);
      setNotifyOn(true);

      // Web Push subscription — delivery even when the app is closed.
      if (isPushSupported()) {
        try {
          const ok = await enablePushSubscription(
            async () => (await pushApi.publicKey()).publicKey,
            (sub) => pushApi.subscribe(sub),
          );
          setPushOn(ok);
          toast.success(ok
            ? 'Notifications enabled — including when the app is closed'
            : 'In-app notifications enabled (push unavailable on this server)');
        } catch (e: any) {
          setPushOn(false);
          toast.success('In-app notifications enabled');
          toast.error(`Closed-app push failed: ${e?.response?.data?.message ?? e.message}`);
        }
      } else {
        toast.success('Notifications enabled while the app is open (this browser lacks push support)');
      }
    } finally {
      setBusy(false);
    }
  };

  const disableNotifications = async () => {
    setBusy(true);
    try {
      setNotifyPref(false);
      setNotifyOn(false);
      await disablePushSubscription((endpoint) => pushApi.unsubscribe(endpoint));
      setPushOn(false);
      toast.success('Browser notifications turned off');
    } finally {
      setBusy(false);
    }
  };

  const sendTestNotification = async () => {
    // Prefer a real server-sent push — that's the same path used when the
    // app is closed. Fall back to a local pop-up if push isn't active.
    if (pushOn) {
      try {
        const { sent } = await pushApi.test();
        if (sent > 0) {
          toast.success(`Test push sent to ${sent} device${sent === 1 ? '' : 's'} — check your system notifications`);
          return;
        }
      } catch {
        // fall through to local
      }
    }
    const shown = showBrowserNotification({
      title: 'ctTicketing test notification',
      body: 'This is how new tickets and updates will pop up.',
      force: true,
    });
    if (shown) toast.success('Test notification shown (local only)');
    else toast.error('Could not show a notification — check browser permission');
  };

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  const updateMutation = useMutation({
    mutationFn: (data: { name: string }) => authApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      toast.success('Profile updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="My Profile"
        intro="Update your display name. Your email is managed by your sign-in and cannot be changed here."
      />

      <div className="max-w-xl card p-6 space-y-4">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input bg-row-odd cursor-not-allowed" value={user?.email ?? ''} disabled />
          <p className="text-xs text-muted mt-1">Email cannot be changed</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => updateMutation.mutate({ name })}
          disabled={updateMutation.isPending || !name.trim() || name === user?.name}
        >
          Save changes
        </button>
      </div>

      {/* Browser notifications */}
      <div className="max-w-xl card p-6 space-y-3">
        <h2 className="section-title flex items-center gap-2">
          {notifyOn && permission === 'granted' ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          Browser notifications
        </h2>
        <p className="text-sm text-muted">
          Get a system pop-up for new tickets, replies, status changes and SLA
          breaches — even when the app tab is closed. Applies to this browser
          on this device; enable it on each device you want notified.
        </p>

        {!isNotifySupported() ? (
          <p className="text-sm text-err">This browser doesn't support notifications.</p>
        ) : (
          <>
            {permission === 'denied' && (
              <p className="text-sm text-err">
                Notifications are blocked for this site — allow them in your
                browser's site settings, then try again.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {notifyOn && permission === 'granted' ? (
                <button className="btn btn-secondary" onClick={disableNotifications} disabled={busy}>
                  Turn off
                </button>
              ) : (
                <button className="btn btn-primary" onClick={enableNotifications} disabled={busy}>
                  {busy ? 'Enabling…' : 'Enable notifications'}
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={sendTestNotification}
                disabled={permission !== 'granted' || busy}
                title={permission !== 'granted' ? 'Enable notifications first' : 'Send a test pop-up'}
              >
                Send test notification
              </button>
            </div>
            <p className="text-xs text-muted">
              Status: {permission === 'granted'
                ? (notifyOn
                    ? (pushOn ? 'On — delivered even when the app is closed' : 'On while the app is open (no push subscription)')
                    : 'Permission granted, currently off')
                : permission === 'denied' ? 'Blocked by browser' : 'Not yet enabled'}
            </p>
            {pushOn && (
              <p className="text-xs text-muted">
                Closed-browser delivery depends on your platform: Windows
                Chrome/Edge deliver while the browser runs in the background
                (Chrome may need &ldquo;Continue running background apps&rdquo; turned
                on); Android delivers even with the browser closed; iPhone
                requires installing this site to the home screen.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
